import type { ConnectorParameterTarget } from '../../types';

const formatModeLabel = ({
  destination,
  bindingKind,
}: ConnectorParameterTarget): string =>
  `${destination === 'project' ? 'Project' : 'Family'} ${bindingKind === 'type' ? 'TYPE' : 'INSTANCE'}`;

/** Builds consent copy from the mode that will actually be applied on Continue. */
export const buildCreateRequiredParametersConsent = (target: ConnectorParameterTarget) => {
  const modeLabel = formatModeLabel(target).toLocaleLowerCase();
  const isProject = target.destination === 'project';
  const isType = target.bindingKind === 'type';

  const firstParagraph = isProject
    ? 'PipeEX creates any missing project parameters in the active Revit project. ' +
      (isType
        ? 'Project TYPE means elements of the same Revit type share one value. ' +
          'For example, five valves of the same Revit type will all have the same code.'
        : 'Project INSTANCE means each placed element stores its own value. ' +
          'For example, two identical valves can have different codes.')
    : 'For loadable families, PipeEX creates any missing family parameters in editable families ' +
      'and reloads the updated families into the project. ' +
      (isType
        ? 'Family TYPE means all instances of the same family type share one value. ' +
          'For example, five valves of the same family type will all have the same code. ' +
          'System families, such as pipes, continue to use corresponding Project TYPE parameters. ' +
          'Pipes of the same type share one value.'
        : 'Family INSTANCE means each placed family instance stores its own value. ' +
          'For example, two identical valves can have different codes. ' +
          'System families, such as pipes, continue to use corresponding Project INSTANCE parameters. ' +
          'Each pipe can have its own value.');

  const storageParagraph =
    'This choice controls how Revit stores parameter values. ' +
    (isProject
      ? 'It does not change the PCF keys mapped on this screen or modify loadable family files.'
      : 'It does not change the PCF keys mapped on this screen.');

  return {
    title: `Confirm ${modeLabel} binding`,
    message:
      `${firstParagraph}\n\n` +
      `${storageParagraph}\n\n` +
      'Your selection is not applied until you select Confirm Binding.',
     confirmLabel: 'Confirm binding',
    cancelLabel: 'Cancel',
    confirmColor: 'warning' as const,
  };
};
